/* PasswordVault — PIN-locked dedicated password vault.
 * Rendered as a subtab inside SecretNotes after the user has already unlocked. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Eye, EyeOff, Copy, RefreshCw, Search, X, KeyRound, Save, Loader2, Globe } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function EntryModal({ initial, pin, onClose, onSaved }) {
  const [form, setForm] = useState({
    site: initial?.site || '',
    username: initial?.username || '',
    password: initial?.password === '••••••••' ? '' : (initial?.password || ''),
    url: initial?.url || '',
    notes: initial?.notes || '',
  });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [genLen, setGenLen] = useState(20);

  const generate = async () => {
    try {
      const r = await axios.get(`${API}/password-vault/generate?length=${genLen}`, { withCredentials: true });
      setForm({ ...form, password: r.data.password });
      setShowPw(true);
      toast.success('🎲 Nytt säkert lösenord genererat');
    } catch { toast.error('Kunde inte generera'); }
  };

  const save = async () => {
    if (!form.site.trim() || !form.password) { toast.error('Webbplats + lösenord krävs'); return; }
    setSaving(true);
    try {
      if (initial?.id) {
        await axios.patch(`${API}/password-vault/${initial.id}`, { pin, ...form }, { withCredentials: true });
      } else {
        await axios.post(`${API}/password-vault/create`, { pin, ...form }, { withCredentials: true });
      }
      toast.success('🔐 Sparat säkert');
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Fel'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="vault-entry-modal">
      <div className="bg-white rounded-3xl max-w-md w-full max-h-[90vh] overflow-y-auto p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold font-['Fredoka'] flex items-center gap-2"><KeyRound className="w-5 h-5" /> {initial?.id ? 'Ändra' : 'Nytt'} lösenord</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100" aria-label="Stäng"><X className="w-5 h-5" /></button>
        </div>
        <label className="block">
          <span className="text-[10px] font-bold uppercase opacity-60">🌐 Webbplats *</span>
          <input value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} className="neo-input w-full text-sm mt-1" placeholder="Instagram, Bank, m.m." data-testid="vault-site" />
        </label>
        <label className="block">
          <span className="text-[10px] font-bold uppercase opacity-60">👤 Användarnamn/email</span>
          <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="neo-input w-full text-sm mt-1" data-testid="vault-username" />
        </label>
        <label className="block">
          <span className="text-[10px] font-bold uppercase opacity-60">🔑 Lösenord *</span>
          <div className="flex gap-1 mt-1">
            <input type={showPw ? 'text' : 'password'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="neo-input flex-1 text-sm font-mono" data-testid="vault-password" />
            <button onClick={() => setShowPw(!showPw)} className="neo-btn px-2 bg-white" data-testid="vault-password-toggle" aria-label={showPw ? 'Dölj' : 'Visa'}>
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <input type="number" min="8" max="64" value={genLen} onChange={(e) => setGenLen(parseInt(e.target.value) || 20)} className="neo-input w-16 text-xs" data-testid="vault-gen-length" />
            <span className="text-[10px] opacity-60">tecken</span>
            <button onClick={generate} className="neo-btn px-2 py-1 bg-gradient-to-r from-purple-300 to-pink-300 text-xs font-bold flex items-center gap-1" data-testid="vault-generate">
              🎲 Slumpa säkert
            </button>
          </div>
        </label>
        <label className="block">
          <span className="text-[10px] font-bold uppercase opacity-60">🔗 URL (frivilligt)</span>
          <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="neo-input w-full text-sm mt-1" placeholder="https://..." />
        </label>
        <label className="block">
          <span className="text-[10px] font-bold uppercase opacity-60">📝 Anteckning</span>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="neo-input w-full text-sm mt-1 resize-none" data-testid="vault-notes" />
        </label>
        <button onClick={save} disabled={saving} className="w-full py-3 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 text-white font-bold flex items-center justify-center gap-2" data-testid="vault-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Spara
        </button>
      </div>
    </div>
  );
}

export default function PasswordVault({ pin }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/password-vault/list`, { pin }, { withCredentials: true });
      setItems(r.data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kunde inte ladda'); }
    setLoading(false);
  }, [pin]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((it) =>
    !search || it.site.toLowerCase().includes(search.toLowerCase()) ||
    (it.username || '').toLowerCase().includes(search.toLowerCase())
  );

  const copyPassword = async (id) => {
    try {
      const r = await axios.post(`${API}/password-vault/reveal/${id}`, { pin }, { withCredentials: true });
      await navigator.clipboard.writeText(r.data.password);
      toast.success('📋 Kopierat till urklipp (försvinner om 30s)');
      setTimeout(() => { try { navigator.clipboard.writeText(''); } catch { /* ignore */ } }, 30000);
    } catch { toast.error('Kunde inte kopiera'); }
  };

  const openEdit = async (id) => {
    try {
      const r = await axios.post(`${API}/password-vault/reveal/${id}`, { pin }, { withCredentials: true });
      setEditing(r.data);
      setModalOpen(true);
    } catch { toast.error('Kunde inte hämta'); }
  };

  const del = async (id, site) => {
    if (!window.confirm(`Ta bort "${site}"?`)) return;
    try {
      await axios.delete(`${API}/password-vault/${id}?pin=${encodeURIComponent(pin)}`, { withCredentials: true });
      toast.success('Borttaget');
      load();
    } catch { toast.error('Kunde inte ta bort'); }
  };

  return (
    <div className="space-y-3" data-testid="password-vault">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 opacity-40" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Sök webbplats/användarnamn" className="neo-input w-full text-sm pl-8" data-testid="vault-search" />
        </div>
        <button onClick={() => { setEditing(null); setModalOpen(true); }} className="neo-btn px-3 py-2 bg-gradient-to-r from-emerald-400 to-teal-500 text-white font-bold text-sm flex items-center gap-1" data-testid="vault-add">
          <Plus className="w-4 h-4" /> Nytt
        </button>
      </div>

      {loading && <div className="text-center py-6 opacity-50 text-xs">Låser upp valvet... 🔐</div>}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-10 space-y-2">
          <div className="text-4xl">🔐</div>
          <p className="text-sm opacity-70 italic">{search ? 'Inga träffar.' : 'Inga sparade lösenord än.'}</p>
          {!search && <p className="text-[11px] opacity-50">Klicka "+ Nytt" för att spara ditt första. Krypteras med din PIN 💛</p>}
        </div>
      )}
      {filtered.map((it) => (
        <div key={it.id} className="neo-card p-3 bg-white flex items-center gap-3" data-testid={`vault-entry-${it.id}`}>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-200 to-pink-200 flex items-center justify-center text-lg flex-shrink-0">🔐</div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm truncate">{it.site}</div>
            <div className="text-[11px] opacity-70 truncate">{it.username || '(inget användarnamn)'}</div>
            {it.url && <a href={it.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5"><Globe className="w-2.5 h-2.5" /> {it.url.replace(/^https?:\/\//, '').slice(0, 30)}</a>}
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <button onClick={() => copyPassword(it.id)} className="p-2 rounded-full bg-emerald-100 hover:bg-emerald-200" title="Kopiera lösenord" data-testid={`vault-copy-${it.id}`}>
              <Copy className="w-4 h-4 text-emerald-700" />
            </button>
            <button onClick={() => openEdit(it.id)} className="p-2 rounded-full bg-blue-100 hover:bg-blue-200" title="Ändra" data-testid={`vault-edit-${it.id}`}>
              <Pencil className="w-4 h-4 text-blue-700" />
            </button>
            <button onClick={() => del(it.id, it.site)} className="p-2 rounded-full bg-red-100 hover:bg-red-200" title="Ta bort" data-testid={`vault-delete-${it.id}`}>
              <Trash2 className="w-4 h-4 text-red-700" />
            </button>
          </div>
        </div>
      ))}

      {modalOpen && (
        <EntryModal
          initial={editing}
          pin={pin}
          onClose={() => { setModalOpen(false); setEditing(null); }}
          onSaved={load}
        />
      )}
    </div>
  );
}
